/**
 * CR054: Admin → Prompt Packs page
 * Manage versioned prompt packs with lifecycle (archive/delete) and pack builder
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Eye, Copy, Download, Check, Loader2, Trash2, Archive, RotateCcw, Pencil, Wrench, Upload } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PackBuilder } from "@/components/admin/prompt-packs/PackBuilder";

// CR229 → CR300: Scan type enum + array support
type ScanType = "FLYER" | "WEBSITE";
// CR466: Pack kind
type PackKind = "SCAN" | "WORKFLOW";

// CR508: Workflow usage stage options (updated from legacy S5.1 → S1.4 scoped)
const WORKFLOW_USAGE_STAGES = [
  { value: "S1_4_LOCATOR", label: "S1.5 — Locate & Crop Locator", contractKey: "flyer_snippet_locator_v1" },
  { value: "S1_4_CROPPER", label: "S1.5 — Locate & Crop Cropper", contractKey: "flyer_snippet_cropper_v1" },
] as const;

// CR508: Workflow filter options for the Prompt Packs list
type WorkflowFilter = "all" | "SCAN_S1" | "SCAN_S2" | "S1_4_LOCATOR" | "S1_4_CROPPER";
const WORKFLOW_FILTER_OPTIONS: { value: WorkflowFilter; label: string }[] = [
  { value: "all", label: "All Workflows" },
  { value: "SCAN_S1", label: "S1 — Flyer AI Scan" },
  { value: "S1_4_LOCATOR", label: "S1.5 — Locate & Crop Locator" },
  { value: "S1_4_CROPPER", label: "S1.5 — Locate & Crop Cropper" },
  { value: "SCAN_S2", label: "S2 — Web Scan" },
];

interface PromptPack {
  id: string;
  name: string;
  description: string | null;
  domain: string;
  version: number;
  is_active: boolean;
  archived_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  scan_type: ScanType; // Legacy - kept for backward compat
  supported_scan_types: ScanType[]; // CR300: Array of supported types
  pack_kind: PackKind; // CR466
  usage_stage: string | null; // CR466
  workflow_contract_key: string | null; // CR466
}

/** Helper: Check if a SCAN pack supports a given scan type. WORKFLOW packs always return false. */
const packSupports = (pack: PromptPack, type: ScanType): boolean =>
  pack.pack_kind === 'SCAN' && (pack.supported_scan_types?.includes(type) ?? pack.scan_type === type);

interface PackUsage {
  snapshotCount: number;
}

export default function PromptPacks() {
  const { isAdmin } = useAuth();
  const [packs, setPacks] = useState<PromptPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showBuilderDialog, setShowBuilderDialog] = useState(false);
  const [newPackName, setNewPackName] = useState("");
  const [newPackDescription, setNewPackDescription] = useState("");
  // CR300: Multi-select for supported scan types
  const [newPackSupportedTypes, setNewPackSupportedTypes] = useState<ScanType[]>(["WEBSITE"]);
  // CR466: Pack kind + workflow fields
  const [newPackKind, setNewPackKind] = useState<PackKind>("SCAN");
  const [newPackUsageStage, setNewPackUsageStage] = useState<string>(WORKFLOW_USAGE_STAGES[0].value);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [workflowFilter, setWorkflowFilter] = useState<WorkflowFilter>("all");
  
  // Selected pack for operations
  const [selectedPack, setSelectedPack] = useState<PromptPack | null>(null);
  const [packToDelete, setPackToDelete] = useState<PromptPack | null>(null);
  const [packUsage, setPackUsage] = useState<PackUsage | null>(null);
  const [checkingUsage, setCheckingUsage] = useState(false);
  // CR469: Soft-delete state
  const [packToSoftDelete, setPackToSoftDelete] = useState<PromptPack | null>(null);
  // CR525: Publish state
  const [packToPublish, setPackToPublish] = useState<PromptPack | null>(null);
  const [availableSnapshots, setAvailableSnapshots] = useState<{ version: number; created_at: string }[]>([]);
  const [publishVersion, setPublishVersion] = useState<number | null>(null);
  const [publishSetActive, setPublishSetActive] = useState(true);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const fetchPacks = async () => {
    try {
      const { data, error } = await supabase
        .from("prompt_packs")
        .select("*")
        .is("deleted_at", null)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      // Cast supported_scan_types and pack_kind from DB
      setPacks((data || []).map(p => ({
        ...p,
        supported_scan_types: (p.supported_scan_types || []) as ScanType[],
        pack_kind: (p.pack_kind || 'SCAN') as PackKind,
      })));
    } catch (err) {
      console.error("Error fetching prompt packs:", err);
      toast.error("Failed to load prompt packs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchPacks();
    }
  }, [isAdmin]);

  const checkPackUsage = async (packId: string): Promise<PackUsage> => {
    const { count } = await supabase
      .from("run_prompt_snapshots")
      .select("id", { count: "exact" })
      .eq("pack_id", packId);
    
    return { snapshotCount: count || 0 };
  };

  const handleSetActive = async (packId: string) => {
    setSaving(true);
    try {
      // First, deactivate all packs in the same domain
      await supabase
        .from("prompt_packs")
        .update({ is_active: false })
        .eq("domain", "gift_card_deals");

      // Then activate the selected pack
      const { error } = await supabase
        .from("prompt_packs")
        .update({ is_active: true })
        .eq("id", packId);

      if (error) throw error;
      toast.success("Pack activated");
      fetchPacks();
    } catch (err) {
      console.error("Error activating pack:", err);
      toast.error("Failed to activate pack");
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePack = async () => {
    if (!newPackName.trim()) {
      toast.error("Pack name is required");
      return;
    }

    setSaving(true);
    try {
      // Get max version
      const { data: existingPacks } = await supabase
        .from("prompt_packs")
        .select("version")
        .eq("domain", "gift_card_deals")
        .order("version", { ascending: false })
        .limit(1);

      const nextVersion = (existingPacks?.[0]?.version || 0) + 1;

      // CR466: Resolve workflow fields
      const isWorkflow = newPackKind === "WORKFLOW";
      const usageStageObj = WORKFLOW_USAGE_STAGES.find(s => s.value === newPackUsageStage);

      const { data: newPack, error } = await supabase
        .from("prompt_packs")
        .insert({
          name: newPackName,
          description: newPackDescription || null,
          domain: "gift_card_deals",
          version: nextVersion,
          is_active: false,
          scan_type: isWorkflow ? "WEBSITE" : (newPackSupportedTypes[0] || "WEBSITE"), // Legacy column
          supported_scan_types: isWorkflow ? [] : newPackSupportedTypes, // CR300
          pack_kind: newPackKind, // CR466
          usage_stage: isWorkflow ? newPackUsageStage : null, // CR466
          workflow_contract_key: isWorkflow ? (usageStageObj?.contractKey || null) : null, // CR466
        })
        .select()
        .single();

      if (error) throw error;

      // CR466 Phase 3: New packs start EMPTY — no auto-seeding of prompt_pack_items

      toast.success("Pack created");
      setShowCreateDialog(false);
      setNewPackName("");
      setNewPackDescription("");
      setNewPackSupportedTypes(["WEBSITE"]); // CR300: Reset to default
      setNewPackKind("SCAN"); // CR466: Reset
      fetchPacks();
    } catch (err) {
      console.error("Error creating pack:", err);
      toast.error("Failed to create pack");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicatePack = async (pack: PromptPack) => {
    setSaving(true);
    try {
      const { data: existingPacks } = await supabase
        .from("prompt_packs")
        .select("version")
        .eq("domain", pack.domain)
        .order("version", { ascending: false })
        .limit(1);

      const nextVersion = (existingPacks?.[0]?.version || 0) + 1;

      const { data: newPack, error } = await supabase
        .from("prompt_packs")
        .insert({
          name: `${pack.name} (Copy)`,
          description: pack.description,
          domain: pack.domain,
          version: nextVersion,
          is_active: false,
          scan_type: pack.scan_type, // Legacy column
          supported_scan_types: pack.supported_scan_types || [pack.scan_type], // CR300
          pack_kind: pack.pack_kind || "SCAN", // CR466
          usage_stage: pack.usage_stage, // CR466
          workflow_contract_key: pack.workflow_contract_key, // CR466
        })
        .select()
        .single();

      if (error) throw error;

      // Copy pack items
      const { data: items } = await supabase
        .from("prompt_pack_items")
        .select("*")
        .eq("prompt_pack_id", pack.id);

      if (items && newPack) {
        const newItems = items.map(({ id, prompt_pack_id, created_at, updated_at, ...rest }) => ({
          ...rest,
          prompt_pack_id: newPack.id,
        }));

        await supabase.from("prompt_pack_items").insert(newItems);
      }

      toast.success("Pack duplicated");
      fetchPacks();
    } catch (err) {
      console.error("Error duplicating pack:", err);
      toast.error("Failed to duplicate pack");
    } finally {
      setSaving(false);
    }
  };

  const handleRenamePack = async () => {
    if (!selectedPack || !newPackName.trim()) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("prompt_packs")
        .update({ 
          name: newPackName.trim(),
          description: newPackDescription || null 
        })
        .eq("id", selectedPack.id);

      if (error) throw error;
      toast.success("Pack renamed");
      setShowRenameDialog(false);
      setSelectedPack(null);
      setNewPackName("");
      setNewPackDescription("");
      fetchPacks();
    } catch (err) {
      console.error("Error renaming pack:", err);
      toast.error("Failed to rename pack");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPack = async (pack: PromptPack) => {
    try {
      const { data: items } = await supabase
        .from("prompt_pack_items")
        .select(`*, prompts (*)`)
        .eq("prompt_pack_id", pack.id);

      const exportData = {
        pack,
        items: items || [],
        exportedAt: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prompt-pack-${pack.name.toLowerCase().replace(/\s+/g, "-")}-v${pack.version}.json`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Pack exported");
    } catch (err) {
      console.error("Error exporting pack:", err);
      toast.error("Failed to export pack");
    }
  };

  const handleArchiveClick = async (pack: PromptPack) => {
    setPackToDelete(pack);
    setCheckingUsage(true);
    const usage = await checkPackUsage(pack.id);
    setPackUsage(usage);
    setCheckingUsage(false);
  };

  // CR469: Soft-delete click handler

  const handleDeleteClick = (pack: PromptPack) => {
    if (pack.is_active) {
      toast.error("Deactivate pack before deleting.");
      return;
    }
    setPackToSoftDelete(pack);
  };

  const handleSoftDelete = async () => {
    if (!packToSoftDelete) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("prompt_packs")
        .update({ deleted_at: new Date().toISOString(), is_active: false })
        .eq("id", packToSoftDelete.id);

      if (error) throw error;
      toast.success("Pack deleted");
      setPackToSoftDelete(null);
      fetchPacks();
    } catch (err) {
      console.error("Error deleting pack:", err);
      toast.error("Failed to delete pack");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!packToDelete) return;
    
    if (packToDelete.is_active) {
      toast.error("Cannot archive the active pack. Please activate another pack first.");
      setPackToDelete(null);
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("prompt_packs")
        .update({ archived_at: new Date().toISOString(), is_active: false })
        .eq("id", packToDelete.id);

      if (error) throw error;
      toast.success("Pack archived");
      setPackToDelete(null);
      setPackUsage(null);
      fetchPacks();
    } catch (err) {
      console.error("Error archiving pack:", err);
      toast.error("Failed to archive pack");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (pack: PromptPack) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("prompt_packs")
        .update({ archived_at: null })
        .eq("id", pack.id);

      if (error) throw error;
      toast.success("Pack restored");
      fetchPacks();
    } catch (err) {
      console.error("Error restoring pack:", err);
      toast.error("Failed to restore pack");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!packToDelete) return;
    
    // Block if active
    if (packToDelete.is_active) {
      toast.error("Deactivate pack before deleting.");
      return;
    }

    setSaving(true);
    try {
      // Soft delete: set deleted_at
      const { error } = await supabase
        .from("prompt_packs")
        .update({ deleted_at: new Date().toISOString(), is_active: false })
        .eq("id", packToDelete.id);

      if (error) throw error;
      toast.success("Pack deleted");
      setPackToDelete(null);
      setPackUsage(null);
      fetchPacks();
    } catch (err) {
      console.error("Error deleting pack:", err);
      toast.error("Failed to delete pack");
    } finally {
      setSaving(false);
    }
  };

  const openRenameDialog = (pack: PromptPack) => {
    setSelectedPack(pack);
    setNewPackName(pack.name);
    setNewPackDescription(pack.description || "");
    setShowRenameDialog(true);
  };

  const openBuilder = (pack: PromptPack) => {
    setSelectedPack(pack);
    setShowBuilderDialog(true);
  };

  // CR525: Publish flow
  const openPublishDialog = async (pack: PromptPack) => {
    setPackToPublish(pack);
    setPublishVersion(null);
    setPublishSetActive(true);
    setLoadingSnapshots(true);
    const { data } = await supabase
      .from("prompt_pack_snapshots")
      .select("version, created_at")
      .eq("prompt_pack_id", pack.id)
      .order("version", { ascending: false });
    setAvailableSnapshots(data || []);
    if (data && data.length > 0) {
      setPublishVersion(data[0].version);
    }
    setLoadingSnapshots(false);
  };

  const handlePublish = async () => {
    if (!packToPublish || publishVersion === null) return;
    setPublishing(true);
    try {
      const { error } = await supabase.rpc("publish_prompt_pack_version", {
        p_pack_id: packToPublish.id,
        p_version: publishVersion,
        p_set_active: publishSetActive,
      });
      if (error) throw error;
      toast.success(`Published v${publishVersion}`);
      setPackToPublish(null);
      fetchPacks();
    } catch (err: any) {
      console.error("Publish error:", err);
      toast.error(err.message || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  // CR508: Filter packs by workflow scope
  const matchesWorkflow = (p: PromptPack): boolean => {
    if (workflowFilter === "all") return true;
    if (workflowFilter === "SCAN_S1") return p.pack_kind === "SCAN" && packSupports(p, "FLYER");
    if (workflowFilter === "SCAN_S2") return p.pack_kind === "SCAN" && packSupports(p, "WEBSITE");
    // Workflow-scoped filters match on usage_stage
    return p.pack_kind === "WORKFLOW" && p.usage_stage === workflowFilter;
  };
  const filteredActivePacks = packs.filter(p => !p.archived_at && matchesWorkflow(p));
  const filteredArchivedPacks = packs.filter(p => !!p.archived_at && matchesWorkflow(p));
  // For tab counts (unfiltered)
  const activePacks = packs.filter(p => !p.archived_at);
  const archivedPacks = packs.filter(p => !!p.archived_at);

  const canDelete = packUsage && packUsage.snapshotCount === 0;

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Prompt Packs</h1>
            <p className="text-muted-foreground">
              Manage versioned collections of prompts for gift card deal scanning
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Pack
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "active" | "archived")}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsList>
              <TabsTrigger value="active">
                Active ({activePacks.length})
              </TabsTrigger>
              <TabsTrigger value="archived">
                Archived ({archivedPacks.length})
              </TabsTrigger>
            </TabsList>

            {/* CR508: Workflow filter */}
            <Select
              value={workflowFilter}
              onValueChange={(v) => setWorkflowFilter(v as WorkflowFilter)}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="All Workflows" />
              </SelectTrigger>
              <SelectContent>
                {WORKFLOW_FILTER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <TabsContent value="active" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <PackTable
                    packs={filteredActivePacks}
                    onSetActive={handleSetActive}
                    onEdit={openBuilder}
                    onRename={openRenameDialog}
                    onDuplicate={handleDuplicatePack}
                    onExport={handleExportPack}
                    onArchive={handleArchiveClick}
                    onDelete={handleDeleteClick}
                    onPublish={openPublishDialog}
                    isArchived={false}
                    saving={saving}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="archived" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <PackTable
                    packs={filteredArchivedPacks}
                    onRestore={handleRestore}
                    onDelete={handleArchiveClick}
                    onExport={handleExportPack}
                    isArchived={true}
                    saving={saving}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Pack Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Prompt Pack</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* CR466: Pack Kind selector */}
              <div>
                <Label>Pack Kind</Label>
                <Select value={newPackKind} onValueChange={(v) => setNewPackKind(v as PackKind)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SCAN">Scan Pack — deal extraction</SelectItem>
                    <SelectItem value="WORKFLOW">Workflow Pack — pipeline task</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={newPackName}
                  onChange={(e) => setNewPackName(e.target.value)}
                  placeholder={newPackKind === "WORKFLOW" ? "S5.1 Snippet Locator v1" : "Gift Card Deals v2"}
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newPackDescription}
                  onChange={(e) => setNewPackDescription(e.target.value)}
                  placeholder={newPackKind === "WORKFLOW" ? "Locate deal snippet regions..." : "Optimized for grocery flyers..."}
                />
              </div>

              {/* CR466: Show scan type selector only for SCAN packs */}
              {newPackKind === "SCAN" && (
                <div>
                  <Label>Use this pack for:</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        id="flyer-type"
                        checked={newPackSupportedTypes.includes("FLYER")}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setNewPackSupportedTypes(prev => [...prev, "FLYER"]);
                          } else {
                            setNewPackSupportedTypes(prev => prev.filter(t => t !== "FLYER"));
                          }
                        }}
                      />
                      <span className="text-sm">Flyer (S1) — PDF extraction</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        id="website-type"
                        checked={newPackSupportedTypes.includes("WEBSITE")}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setNewPackSupportedTypes(prev => [...prev, "WEBSITE"]);
                          } else {
                            setNewPackSupportedTypes(prev => prev.filter(t => t !== "WEBSITE"));
                          }
                        }}
                      />
                      <span className="text-sm">Website (S2) — Web scraping</span>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Controls which source type dropdowns this pack appears in during batch runs.
                  </p>
                  {newPackSupportedTypes.length === 0 && (
                    <p className="text-xs text-destructive mt-1">
                      Select at least one source type.
                    </p>
                  )}
                </div>
              )}

              {/* CR466: Show workflow usage stage for WORKFLOW packs */}
              {newPackKind === "WORKFLOW" && (
                <div>
                  <Label>Usage Stage</Label>
                  <Select value={newPackUsageStage} onValueChange={setNewPackUsageStage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WORKFLOW_USAGE_STAGES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Determines which pipeline step will use this pack.
                  </p>
                </div>
              )}

              <p className="text-xs text-muted-foreground border-t pt-3">
                Pack will be created empty. Use the Pack Builder to add prompts.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreatePack} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename Pack Dialog */}
        <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rename Pack</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="rename-name">Name</Label>
                <Input
                  id="rename-name"
                  value={newPackName}
                  onChange={(e) => setNewPackName(e.target.value)}
                  placeholder="Pack name"
                />
              </div>
              <div>
                <Label htmlFor="rename-description">Description</Label>
                <Textarea
                  id="rename-description"
                  value={newPackDescription}
                  onChange={(e) => setNewPackDescription(e.target.value)}
                  placeholder="Description..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRenameDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleRenamePack} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pack Builder Dialog */}
        <Dialog open={showBuilderDialog} onOpenChange={setShowBuilderDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            {selectedPack && (
              <PackBuilder
                packId={selectedPack.id}
                packName={selectedPack.name}
                packKind={selectedPack.pack_kind || 'SCAN'}
                onClose={() => {
                  setShowBuilderDialog(false);
                  setSelectedPack(null);
                }}
                onSave={() => {
                  fetchPacks();
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Archive/Delete Confirmation Dialog */}
        <AlertDialog open={!!packToDelete} onOpenChange={(open) => !open && setPackToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {packToDelete?.archived_at ? "Delete Prompt Pack" : "Archive Prompt Pack"}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    {packToDelete?.archived_at 
                      ? `Are you sure you want to permanently delete "${packToDelete?.name}" (v${packToDelete?.version})?`
                      : `Are you sure you want to archive "${packToDelete?.name}" (v${packToDelete?.version})?`
                    }
                  </p>
                  
                  {checkingUsage ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking usage...
                    </div>
                  ) : packUsage && (
                    <div className="text-sm space-y-1 bg-muted p-3 rounded">
                      <p>• Referenced in <strong>{packUsage.snapshotCount}</strong> run snapshot(s)</p>
                      {!canDelete && packToDelete?.archived_at && (
                        <p className="text-destructive font-medium mt-2">
                          Cannot delete: This pack is referenced by run snapshots and must be preserved for historical accuracy.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {packToDelete?.archived_at ? (
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={!canDelete || saving}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Delete Permanently
                </AlertDialogAction>
              ) : (
                <AlertDialogAction 
                  onClick={handleArchive} 
                  disabled={saving || packToDelete?.is_active}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Archive
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* CR469: Soft-Delete Confirmation Dialog */}
        <AlertDialog open={!!packToSoftDelete} onOpenChange={(open) => !open && setPackToSoftDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Prompt Pack</AlertDialogTitle>
              <AlertDialogDescription>
                Delete "{packToSoftDelete?.name}" (v{packToSoftDelete?.version})? The pack will be hidden from all lists and selectors. This can be recovered if needed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleSoftDelete}
                disabled={saving}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* CR525: Publish Version Dialog */}
        <Dialog open={!!packToPublish} onOpenChange={(open) => !open && setPackToPublish(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Publish Version — {packToPublish?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {loadingSnapshots ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading snapshots...
                </div>
              ) : availableSnapshots.length === 0 ? (
                <p className="text-sm text-muted-foreground">No snapshots found for this pack. Create a snapshot first.</p>
              ) : (
                <>
                  <div>
                    <Label>Snapshot Version</Label>
                    <Select
                      value={publishVersion !== null ? String(publishVersion) : ""}
                      onValueChange={(v) => setPublishVersion(Number(v))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select version..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSnapshots.map((s) => (
                          <SelectItem key={s.version} value={String(s.version)}>
                            v{s.version} — {new Date(s.created_at).toLocaleDateString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="publish-set-active"
                      checked={publishSetActive}
                      onCheckedChange={(c) => setPublishSetActive(!!c)}
                    />
                    <Label htmlFor="publish-set-active" className="text-sm cursor-pointer">
                      Also set as active pack (for its usage stage)
                    </Label>
                  </div>
                  {packToPublish && publishVersion !== null && publishVersion !== packToPublish.version && (
                    <p className="text-xs text-muted-foreground">
                      Current published version: v{packToPublish.version} → will become v{publishVersion}
                    </p>
                  )}
                </>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPackToPublish(null)}>Cancel</Button>
                <Button
                  onClick={handlePublish}
                  disabled={publishing || publishVersion === null || availableSnapshots.length === 0}
                >
                  {publishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Upload className="mr-2 h-4 w-4" />
                  Publish v{publishVersion ?? "?"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

// Extracted table component
function PackTable({
  packs,
  onSetActive,
  onEdit,
  onRename,
  onDuplicate,
  onExport,
  onArchive,
  onRestore,
  onDelete,
  onPublish,
  isArchived,
  saving,
}: {
  packs: PromptPack[];
  onSetActive?: (id: string) => void;
  onEdit?: (p: PromptPack) => void;
  onRename?: (p: PromptPack) => void;
  onDuplicate?: (p: PromptPack) => void;
  onExport?: (p: PromptPack) => void;
  onArchive?: (p: PromptPack) => void;
  onRestore?: (p: PromptPack) => void;
  onDelete?: (p: PromptPack) => void;
  onPublish?: (p: PromptPack) => void;
  isArchived: boolean;
  saving?: boolean;
}) {
  if (packs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {isArchived ? "No archived packs" : "No packs found"}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Version</TableHead>
          {!isArchived && <TableHead>Status</TableHead>}
          <TableHead>Updated</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {packs.map((pack) => (
          <TableRow key={pack.id} className={isArchived ? "opacity-60" : ""}>
            <TableCell className="font-medium">{pack.name}</TableCell>
            <TableCell>
              {/* CR466: Show pack_kind + type badges */}
              <div className="flex gap-1 flex-wrap">
              {pack.pack_kind === "WORKFLOW" ? (
                  <Badge variant="outline" className="border-rose-400 text-rose-600">
                    {pack.usage_stage === "S1_4_LOCATOR" ? "S1.5 Locator" : pack.usage_stage === "S1_4_CROPPER" ? "S1.5 Cropper" : pack.usage_stage || "Workflow"}
                  </Badge>
                ) : (
                  (pack.supported_scan_types || [pack.scan_type]).map((type) => (
                    <Badge key={type} variant={type === "FLYER" ? "secondary" : "outline"}>
                      {type === "FLYER" ? "S1" : "S2"}
                    </Badge>
                  ))
                )}
              </div>
            </TableCell>
            <TableCell>v{pack.version}</TableCell>
            {!isArchived && (
              <TableCell>
                {pack.is_active ? (
                  <Badge className="bg-green-500">Active</Badge>
                ) : (
                  onSetActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSetActive(pack.id)}
                      disabled={saving}
                    >
                      Set Active
                    </Button>
                  )
                )}
              </TableCell>
            )}
            <TableCell className="text-muted-foreground text-sm">
              {new Date(pack.updated_at).toLocaleDateString()}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-1">
                {isArchived ? (
                  <>
                    {onRestore && (
                      <Button variant="ghost" size="icon" onClick={() => onRestore(pack)} title="Restore">
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                    {onExport && (
                      <Button variant="ghost" size="icon" onClick={() => onExport(pack)} title="Export">
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {onDelete && (
                      <Button variant="ghost" size="icon" onClick={() => onDelete(pack)} title="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {onPublish && (
                      <Button variant="ghost" size="icon" onClick={() => onPublish(pack)} title="Publish Version">
                        <Upload className="h-4 w-4" />
                      </Button>
                    )}
                    {onEdit && (
                      <Button variant="ghost" size="icon" onClick={() => onEdit(pack)} title="Build Pack">
                        <Wrench className="h-4 w-4" />
                      </Button>
                    )}
                    {onRename && (
                      <Button variant="ghost" size="icon" onClick={() => onRename(pack)} title="Rename">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {onDuplicate && (
                      <Button variant="ghost" size="icon" onClick={() => onDuplicate(pack)} title="Duplicate">
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                    {onExport && (
                      <Button variant="ghost" size="icon" onClick={() => onExport(pack)} title="Export">
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {onArchive && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => onArchive(pack)} 
                        disabled={pack.is_active}
                        title={pack.is_active ? "Cannot archive active pack" : "Archive"}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                    {onDelete && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => onDelete(pack)} 
                        disabled={pack.is_active}
                        title={pack.is_active ? "Deactivate pack before deleting" : "Delete"}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
